"use client";

import { useEffect, useState } from "react";
import { getFeatures, type FeatureFlags } from "@/lib/api/api-client";

/**
 * Lecture d'un interrupteur produit administrable.
 *
 * # Un seul appel pour toute la session
 *
 * Les flags ne bougent que quand un admin les manœuvre, donc le résultat vit
 * au niveau du module et non dans un état de composant : le second consommateur
 * ne redemande rien, et deux composants montés dans la même frame partagent la
 * promesse en vol plutôt que d'ouvrir deux requêtes.
 *
 * Le corollaire est assumé : un basculement en admin n'atteint les onglets déjà
 * ouverts qu'au prochain rechargement. Pour un réglage éditorial qu'on touche
 * une fois par trimestre, du polling coûterait plus cher que le délai.
 *
 * # Le défaut est `false`
 *
 * Avant la réponse, et si l'appel échoue, tout flag vaut « éteint ». C'est ce
 * qui rend l'appel non bloquant : rien n'attend le réseau pour peindre, et une
 * panne d'API dégrade vers l'écran sans le supplément — jamais vers un écran
 * cassé.
 */

let cache: FeatureFlags | null = null;
let inFlight: Promise<FeatureFlags> | null = null;

function loadFlags(): Promise<FeatureFlags> {
  if (cache !== null) {
    return Promise.resolve(cache);
  }

  inFlight ??= getFeatures()
    .then((flags) => {
      cache = flags;

      return flags;
    })
    .catch(() => {
      // Une panne de flags ne doit pas se voir : on retombe sur les défauts, et
      // on ne mémorise pas l'échec pour qu'un montage ultérieur puisse réessayer.
      inFlight = null;

      return {};
    });

  return inFlight;
}

export function useFeatureFlag(name: keyof FeatureFlags): boolean {
  const [enabled, setEnabled] = useState<boolean>(() => cache?.[name] ?? false);

  useEffect(() => {
    if (cache !== null) {
      return;
    }

    let cancelled = false;

    loadFlags().then((flags) => {
      if (cancelled) {
        return;
      }

      setEnabled(flags[name] ?? false);
    });

    return () => {
      cancelled = true;
    };
  }, [name]);

  return enabled;
}

/** Réservé aux tests : repart d'un cache vide entre deux cas. */
export function resetFeatureFlagCache(): void {
  cache = null;
  inFlight = null;
}
